// MARK://dhk8/admin-shell/components/SectionedSheet
// Structured modal on top of Sheet.tsx. Splits a form into labelled sections so
// dialogs stop looking "flat". `FormSection` renders a mono-uppercase eyebrow
// (same look as the `ing-section` pattern already used across pages) followed by
// its fields; `SectionedSheet` re-exposes Sheet with the body laid out as a
// vertical stack ready to receive <FormSection> children.
//
// Self-contained: only depends on Sheet.tsx + tokens.css. All styling lives in a
// scoped <style> block using `nui-*` classes (never touches styles.css/tokens.css).
import type { ReactNode } from 'react'
import Sheet from './Sheet'

const SECTIONED_SHEET_CSS = `
.nui-ss-body { display: flex; flex-direction: column; gap: var(--pos-space-2, 8px); }
.nui-ss-section { display: flex; flex-direction: column; gap: var(--pos-space-2, 8px); }
.nui-ss-section + .nui-ss-section { margin-top: var(--pos-space-3, 12px); }
.nui-ss-section__title {
  font-family: var(--font-mono, var(--font-sans));
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--pos-fg-tertiary);
  margin: 6px 0 2px;
}
.nui-ss-section:first-child .nui-ss-section__title { margin-top: 0; }
.nui-ss-section__body { display: flex; flex-direction: column; gap: var(--pos-space-3, 12px); }
`

export interface FormSectionProps {
  title: string
  children: ReactNode
}

/** A labelled block inside a SectionedSheet (mono-uppercase eyebrow + fields). */
export function FormSection({ title, children }: FormSectionProps) {
  return (
    <section className="nui-ss-section">
      <div className="nui-ss-section__title">{title}</div>
      <div className="nui-ss-section__body">{children}</div>
    </section>
  )
}

export interface SectionedSheetProps {
  open: boolean
  title: string
  onClose: () => void
  footer?: ReactNode
  children: ReactNode
}

/** Sheet wrapper whose body is a vertical stack meant to hold <FormSection>s. */
export default function SectionedSheet({ open, title, onClose, footer, children }: SectionedSheetProps) {
  return (
    <Sheet open={open} title={title} onClose={onClose} footer={footer}>
      <style>{SECTIONED_SHEET_CSS}</style>
      <div className="nui-ss-body">{children}</div>
    </Sheet>
  )
}
